import React, { useEffect, useMemo, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import * as mutations from '../graphql/mutations';
import useOperationalStore from '../platform/store/useOperationalStore';
import useShopSettings from '../platform/settings/useShopSettings';
import { activeEstimate, invoiceDueMs, invoiceOverdueMs, estimateApprovalUrl, paidToDate, remainingBalance } from '../platform/engine/estimateMoney';
import { getCurrentRole } from '../shared/permissions';
import { nowSeconds } from '../platform/events/operationEvents';
import './CollectionsView.css';

const client = generateClient();

const PAYMENT_METHODS = [
  { value: 'check',         label: 'Check' },
  { value: 'card',          label: 'Card' },
  { value: 'cash',          label: 'Cash' },
  { value: 'ach',           label: 'ACH / Wire' },
  { value: 'fleet_account', label: 'Fleet Account' },
  { value: 'other',         label: 'Other' },
];

const DAY_MS = 86_400_000;

function fmtCurrency(n) {
  if (!n) return '$0.00';
  return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function fmtDate(ts) {
  if (!ts) return '—';
  const ms = ts < 10_000_000_000 ? ts * 1000 : ts;
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function stripTypename(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripTypename);
  const { __typename, ...rest } = obj;
  return Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, stripTypename(v)]));
}

export default function CollectionsView() {
  const trucks = useOperationalStore(s => s.trucks);
  const fetchAll = useOperationalStore(s => s.fetchAll);
  const localUpdateTruck = useOperationalStore(s => s.localUpdateTruck);
  const setSelected = useOperationalStore(s => s.setSelectedTruckId);
  const business = useShopSettings(s => s.settings.business);

  const [now] = useState(Date.now());
  const [payingId, setPayingId] = useState(null); // truck id with the payment form open
  const [payForm, setPayForm] = useState({ amount: '', method: 'check', reference: '' });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  // Optimistic: ids marked paid this session so rows drop without waiting on a refetch
  const [paidIds, setPaidIds] = useState(() => new Set());

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Unpaid invoices across ALL ROs — completed jobs still owe money.
  const rows = useMemo(() => {
    return trucks
      .filter(t => !paidIds.has(t.id))
      .map(truck => {
        const est = activeEstimate(truck);
        if (!est || est.status !== 'invoiced') return null;
        const total = parseFloat(est.total) || 0;
        const paid = paidToDate(est);
        const remaining = remainingBalance(est);
        if (remaining <= 0) return null; // fully collected, status just lagging
        const dueMs = invoiceDueMs(est);
        const overdueMs = invoiceOverdueMs(est, now);
        return { truck, est, total, paid, remaining, dueMs, overdueMs };
      })
      .filter(Boolean)
      .sort((a, b) => b.overdueMs - a.overdueMs || (a.dueMs || 0) - (b.dueMs || 0));
  }, [trucks, now, paidIds]);

  const outstanding = rows.reduce((s, r) => s + r.remaining, 0);
  const overdueTotal = rows.filter(r => r.overdueMs > 0).reduce((s, r) => s + r.remaining, 0);
  const overdueCount = rows.filter(r => r.overdueMs > 0).length;

  function flash(msg) {
    setNotice(msg);
    setTimeout(() => setNotice(''), 4000);
  }

  async function markPaid(row) {
    setBusy(true);
    setError('');
    const { truck, est, remaining } = row;
    const methodLabel = PAYMENT_METHODS.find(m => m.value === payForm.method)?.label || payForm.method;
    const ts = nowSeconds();

    const requested = parseFloat(payForm.amount);
    const applied = Number.isFinite(requested) && requested > 0 ? Math.min(requested, remaining) : remaining;
    const remainingAfter = Math.max(0, remaining - applied);

    const entry = {
      id: `pay-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      amount: applied,
      method: payForm.method,
      reference: payForm.reference.trim() || null,
      note: null,
      receivedAt: ts,
      recordedBy: getCurrentRole(),
    };
    const nextEst = {
      ...stripTypename(est),
      payments: [...stripTypename(est.payments || []), entry],
      status: remainingAfter <= 0.005 ? 'paid' : 'invoiced',
      updatedAt: ts,
    };
    const estimates = stripTypename(truck.estimates || []).map(item => (item.id === est.id ? nextEst : item));
    const activity = {
      id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type: 'authorization',
      text: `Payment recorded - ${fmtCurrency(applied)} - ${methodLabel}${entry.reference ? ` #${entry.reference}` : ''}${remainingAfter > 0 ? ` - ${fmtCurrency(remainingAfter)} remaining` : ' - PAID IN FULL'}`,
      author: getCurrentRole(),
      timestamp: ts,
      customerFacing: true,
      stage: truck.status || null,
    };
    try {
      localUpdateTruck(truck.id, { estimates }); // row updates instantly
      await client.graphql({
        query: mutations.updateTruck,
        variables: {
          input: {
            id: truck.id,
            estimates,
            activities: [activity, ...stripTypename(truck.activities || [])],
            lastNote: ts,
          },
        },
      });
      if (remainingAfter <= 0.005) {
        setPaidIds(prev => new Set(prev).add(truck.id));
        flash(`${fmtCurrency(applied)} from ${truck.companyName} — paid in full (${methodLabel}).`);
      } else {
        flash(`${fmtCurrency(applied)} from ${truck.companyName} recorded (${methodLabel}) — ${fmtCurrency(remainingAfter)} still owed.`);
      }
      setPayingId(null);
      setPayForm({ amount: '', method: 'check', reference: '' });
    } catch (err) {
      console.error('mark paid failed', err);
      setError('Could not record the payment — check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  function sendReminder(e, row) {
    e.stopPropagation();
    const { truck, est, remaining, overdueMs } = row;
    const shopName = business?.shopName || business?.companyName || 'REKON';
    const lateBit = overdueMs > 0 ? ` (${Math.floor(overdueMs / DAY_MS)} days past due)` : '';
    const link = est.token ? ` View: ${estimateApprovalUrl(est.token)}` : '';
    const message = `${shopName}: invoice for RO ${truck.repairOrder} — ${fmtCurrency(remaining)} is outstanding${lateBit}. Please call the shop to arrange payment.${link}`;
    const phone = (truck.phone || '').replace(/[^\d+]/g, '');
    if (phone) {
      window.location.href = `sms:${encodeURIComponent(phone)}?&body=${encodeURIComponent(message)}`;
    } else if (truck.email) {
      window.location.href = `mailto:${encodeURIComponent(truck.email)}?subject=${encodeURIComponent(`Outstanding invoice - RO ${truck.repairOrder}`)}&body=${encodeURIComponent(message)}`;
    } else {
      navigator.clipboard?.writeText(message).catch(() => {});
      flash('No phone or email on the RO — reminder text copied to clipboard.');
    }
  }

  return (
    <div className="cv-root">
      <div className="cv-header">
        <div>
          <h1>Collections</h1>
          <p>Every unpaid invoice, worst first. Completed jobs still owe money.</p>
        </div>
      </div>

      <div className="cv-summary">
        <div className="cv-sum-card">
          <span className="cv-sum-label">Outstanding</span>
          <span className="cv-sum-value">{fmtCurrency(outstanding)}</span>
          <span className="cv-sum-sub">{rows.length} invoice{rows.length === 1 ? '' : 's'}</span>
        </div>
        <div className={`cv-sum-card${overdueTotal > 0 ? ' cv-sum-card--bad' : ''}`}>
          <span className="cv-sum-label">Overdue</span>
          <span className="cv-sum-value">{fmtCurrency(overdueTotal)}</span>
          <span className="cv-sum-sub">{overdueCount} past terms</span>
        </div>
      </div>

      {error && <div className="cv-banner cv-banner--error">{error}</div>}
      {notice && <div className="cv-banner cv-banner--ok">{notice}</div>}

      <div className="cv-table-card">
        {rows.length === 0 ? (
          <div className="cv-empty">Nothing outstanding. Every invoice is paid.</div>
        ) : (
          <table className="cv-table">
            <thead>
              <tr>
                <th>RO / Customer</th>
                <th>Amount</th>
                <th>Invoiced</th>
                <th>Terms</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const { truck, est, total, paid, remaining, dueMs, overdueMs } = row;
                const overdueDays = Math.floor(overdueMs / DAY_MS);
                const dueInDays = dueMs ? Math.ceil((dueMs - now) / DAY_MS) : null;
                const isPaying = payingId === truck.id;
                return (
                  <React.Fragment key={truck.id}>
                    <tr onClick={() => setSelected(truck.id)}>
                      <td>
                        <div className="cv-main">{truck.repairOrder || '—'} · {truck.companyName || '—'}</div>
                        <div className="cv-sub">{truck.phone || truck.email || 'no contact on file'}</div>
                      </td>
                      <td className="cv-amount">
                        {fmtCurrency(remaining)}
                        {paid > 0 && <div className="cv-sub">of {fmtCurrency(total)} · {fmtCurrency(paid)} paid</div>}
                      </td>
                      <td>{fmtDate(est.invoicedAt)}</td>
                      <td>Net {est.paymentTermsDays || 30}</td>
                      <td>
                        {overdueMs > 0 ? (
                          <span className="cv-status cv-status--overdue">Overdue {overdueDays}d</span>
                        ) : (
                          <span className="cv-status cv-status--current">Due in {Math.max(0, dueInDays)}d</span>
                        )}
                      </td>
                      <td className="cv-actions">
                        <button
                          disabled={busy}
                          onClick={e => {
                            e.stopPropagation();
                            if (isPaying) { setPayingId(null); return; }
                            setPayForm(f => ({ ...f, amount: remaining.toFixed(2) }));
                            setPayingId(truck.id);
                          }}
                        >
                          {isPaying ? 'Cancel' : 'Record Payment'}
                        </button>
                        <button disabled={busy} onClick={e => sendReminder(e, row)}>Remind</button>
                      </td>
                    </tr>
                    {isPaying && (
                      <tr className="cv-pay-row">
                        <td colSpan={6}>
                          <div className="cv-pay-form" onClick={e => e.stopPropagation()}>
                            <input
                              className="cv-pay-amount"
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder={`Amount (owed ${fmtCurrency(remaining)})`}
                              value={payForm.amount}
                              onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                            />
                            <select value={payForm.method}
                              onChange={e => setPayForm(f => ({ ...f, method: e.target.value }))}>
                              {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                            <input
                              placeholder="Reference / check # (optional)"
                              value={payForm.reference}
                              onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))}
                            />
                            <button className="cv-confirm" disabled={busy} onClick={() => markPaid(row)}>
                              {busy ? 'Saving…' : `Record ${fmtCurrency(Math.min(parseFloat(payForm.amount) || remaining, remaining))}`}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
